import {
  type ComponentPropsWithoutRef,
  type FC,
  type FormEventHandler,
  useRef,
} from 'react'
import { FormattedMessage } from 'react-intl'

import { Notification } from 'base-ui'

import {
  useOpenableControls,
  useRerenderAndExecute,
} from '../../../../../generic-hooks'
import { useGlobalConfigContext } from '../../../../../global-config'
import { gtmDataLayerPushHomeOneAddToCart } from '../../../../../gtm'
import { urlResolverGetCartIntermediatePage } from '../../../../../url-handling'
import { productAccessorGetVariantCode } from '../../../../model/productAccessor/productAccessorGetVariantCode'
import { ProductPageRxByPrincipleDialog } from '../../../../rx-by-principle'
import { useProductPageContext } from '../../../productPageContext'
import { ProductPagePrescriptionTypeSelectionModal } from '../ProductPagePrescriptionTypeSelectionModal/ProductPagePrescriptionTypeSelectionModal'

import { AddToCartButton } from './ProductAddToCartButton'
import { useBuildAddToCartParams } from './useBuildAddToCartParams'
import { useProductAddToCartCtaShouldShowSelectErxModal } from './useProductAddToCartCtaShouldShowSelectErxModal'

export const PRODUCT_ADD_TO_CART_FORM_ID = 'product-add-to-cart-form-id'

type ProductAddToCartCtaProps = Omit<
  ComponentPropsWithoutRef<'form'>,
  'action' | 'id' | 'onSubmit'
>

export const ProductAddToCartCta: FC<ProductAddToCartCtaProps> = (props) => {
  const formRef = useRef<HTMLFormElement>(null)

  const { attributeSelectionControls, product, rxByPrincipleControls } =
    useProductPageContext()
  const shouldShowErxModalOnAddToCart =
    useProductAddToCartCtaShouldShowSelectErxModal()
  const globalConfig = useGlobalConfigContext()
  const erxTypeSelectModalControls = useOpenableControls()
  const { addToCartParams, isPrivatePrescriptionAndCartMetadataHasErx } =
    useBuildAddToCartParams()
  const rxByPrincipleDialogControls = useOpenableControls()
  const rerenderAndExecute = useRerenderAndExecute()
  const onSubmit: FormEventHandler<HTMLFormElement> = (event): void => {
    if (
      !attributeSelectionControls.attributeNotSelectedErrorControls.validateWhenAddingToCart()
    ) {
      event.preventDefault()

      return
    }

    if (shouldShowErxModalOnAddToCart) {
      event.preventDefault()
      erxTypeSelectModalControls.handleOpen()

      return
    }

    if (
      rxByPrincipleControls.isReasonRequired &&
      !rxByPrincipleControls.selectedReason
    ) {
      event.preventDefault()
      rxByPrincipleDialogControls.handleOpen()

      return
    }

    gtmDataLayerPushHomeOneAddToCart()
  }

  return (
    <>
      <form
        {...props}
        action={urlResolverGetCartIntermediatePage(globalConfig)}
        id={PRODUCT_ADD_TO_CART_FORM_ID}
        onSubmit={onSubmit}
        ref={formRef}
      >
        {Object.entries(
          (addToCartParams as unknown as Record<string, string> | undefined) ??
            {},
        ).map(([name, value]) => {
          return <input key={name} name={name} type="hidden" value={value} />
        })}
        <AddToCartButton />
        {isPrivatePrescriptionAndCartMetadataHasErx ? (
          <Notification
            className="mt-2"
            data-qa-id="add-to-cart-cta-notification"
            severity="info"
          >
            <FormattedMessage id="pdp.messages.info.privatePrescriptionNotCombinableWithERX" />
          </Notification>
        ) : null}
      </form>
      <ProductPagePrescriptionTypeSelectionModal
        controls={erxTypeSelectModalControls}
      />
      {rxByPrincipleControls.isReasonRequired ? (
        <ProductPageRxByPrincipleDialog
          autoOpenOnLocationHash
          // key is needed to reset form state when user clientside navigates to other product
          key={productAccessorGetVariantCode(product)}
          onClose={rxByPrincipleDialogControls.handleClose}
          onOpen={rxByPrincipleDialogControls.handleOpen}
          onReasonSelect={(reason) => {
            rxByPrincipleDialogControls.handleClose()
            rxByPrincipleControls.setSelectedReason(reason)
            // ensure all state is updated
            rerenderAndExecute(() => {
              formRef.current?.requestSubmit()
            })
          }}
          open={rxByPrincipleDialogControls.isOpen}
          productName={product.name}
          reasonSet={rxByPrincipleControls.reasonSet}
        />
      ) : null}
    </>
  )
}
